import {PaymentIntent} from '@stripe/stripe-js'
import {Invoice} from 'organization/Invoices/Invoices/OrganizationInvoicesProvider'
import {organizationApiClient} from 'organization/organizationApiClient'
import {useOrganization} from 'organization/providers/OrganizationProvider'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'

export type Transaction = PaymentIntent & {
  invoice: Invoice | null
}

export interface OrganizationTransactionsContextProps {
  transactions: Transaction[]
  createInvoice: (transaction: Transaction) => Promise<boolean>
}

export const OrganizationTransactionsContext = React.createContext<
  OrganizationTransactionsContextProps | undefined
>(undefined)

export default function OrganizationTransactionsProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const {organization} = useOrganization()

  useEffect(() => {
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async () => {
    const response = await apiClient.get(
      `api/organization/${organization?.id}/transactions`,
    )
    setTransactions(response)
    setLoading(false)
  }

  const createInvoice = async (transaction: Transaction) => {
    const result = await apiClient
      .post(
        `api/organization/${organization?.id}/transactions/create-invoice`,
        {
          transaction_id: transaction.id,
        },
      )
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  return (
    <OrganizationTransactionsContext.Provider
      value={{
        transactions,
        createInvoice,
      }}
    >
      {loading && <FullPageLoader />}
      {!loading && props.children}
    </OrganizationTransactionsContext.Provider>
  )
}

export function useOrganizationTransactions() {
  const context = React.useContext(OrganizationTransactionsContext)
  if (context === undefined) {
    throw new Error(
      'useOrganizationTransactions must be used within OrganizationTransactionsProvider',
    )
  }
  return context
}
